/**
 * Fit Mass into a road trip. Pure: no I/O, no clock, no DOM.
 *
 * The analysis (docs/analysis.md) shows why this is easy despite looking like
 * vehicle routing: Mass times are fixed, so every feasible plan moves strictly
 * forward in time and the candidates form a DAG.
 *
 * - {@link rankSingleMass}: one Mass on the way. A scan; returns every option,
 *   best first.
 * - {@link planTrip}: one Mass per day (or per day of obligation) on a longer
 *   trip. A DAG longest/shortest path.
 *
 * Both honour exactly one time constraint, as Google Maps does: depart at a
 * time, or arrive by a time (in which case the answer includes when to leave).
 *
 * Times are epoch milliseconds; durations are minutes.
 */

import { addDays, dateKeyOf, instantOf, MINUTE, wallTimeAt, type DateKey } from "@viamissa/time";

/** Be in the pew this many minutes before Mass starts. */
export const ARRIVE_EARLY_MIN = 15;

/** One Mass at one church, positioned on the route. */
export type Candidate = {
  id: string;
  churchId: string;
  /** Mass start, epoch ms. */
  start: number;
  /** Minutes allotted to the Mass itself (60 Sunday/vigil, 30 weekday). */
  minutes: number;
  /** One Mass per distinct key: the date (daily) or the day of obligation (Sundays). */
  dayKey: string;
  /** Drive minutes from the trip's start to where the church joins the route. */
  alongMin: number;
  /** Detour, minutes: route → church, and church → route. */
  toMin: number;
  backMin: number;
};

export type Constraint = { kind: "depart"; at: number } | { kind: "arrive"; by: number };

/**
 * How driving turns minutes into clock time. The default drives straight
 * through; {@link drivingWindow} pauses overnight. Both directions must agree:
 * `forward(backward(t, m), m) <= t`.
 */
export type DriveClock = {
  forward(from: number, minutes: number): number;
  backward(arriveBy: number, minutes: number): number;
};

export const NONSTOP: DriveClock = {
  forward: (from, minutes) => from + minutes * MINUTE,
  backward: (by, minutes) => by - minutes * MINUTE,
};

/**
 * Drive only between `startHour` and `endHour` local time in `timeZone`
 * (e.g. 8–20). Time-dependent but FIFO: leaving later never arrives earlier,
 * which is what keeps the DAG recurrence exact (analysis §3).
 */
export function drivingWindow(options: { startHour: number; endHour: number; timeZone: string }): DriveClock {
  const { startHour, endHour, timeZone } = options;
  const bounds = (instant: number, dayOffset: number) => {
    const wall = wallTimeAt(instant, timeZone);
    const date = addDays(dateKeyOf(wall), dayOffset);
    const [year, month, day] = date.split("-").map(Number) as [number, number, number];
    return {
      open: instantOf({ year, month, day, hour: startHour, minute: 0 }, timeZone),
      close: instantOf({ year, month, day, hour: endHour, minute: 0 }, timeZone),
    };
  };

  return {
    forward(from, minutes) {
      let now = from;
      let left = minutes * MINUTE;
      for (let guard = 0; guard < 400; guard++) {
        let { open, close } = bounds(now, 0);
        if (now >= close) ({ open, close } = bounds(now, 1));
        if (now < open) now = open;
        if (left <= close - now) return now + left;
        left -= close - now;
        now = close;
      }
      return now + left;
    },
    backward(by, minutes) {
      let now = by;
      let left = minutes * MINUTE;
      for (let guard = 0; guard < 400; guard++) {
        let { open, close } = bounds(now, 0);
        if (now <= open) ({ open, close } = bounds(now, -1));
        if (now > close) now = close;
        if (left <= now - open) return now - left;
        left -= now - open;
        now = open;
      }
      return now - left;
    },
  };
}

export type TripInput = {
  /** `R(L)`: drive minutes for the whole route without stopping. */
  totalMin: number;
  constraint: Constraint;
  /** Extra minutes added to every drive, for traffic the router cannot see. */
  marginMin?: number;
  /** Minutes to be seated before Mass. Defaults to {@link ARRIVE_EARLY_MIN}. */
  arriveEarlyMin?: number;
  /**
   * Depart at: leave out a first Mass that would mean waiting longer than this
   * at the church. Such a Mass is not on the way so much as a reason to leave
   * later. Defaults to no limit.
   */
  maxWaitMin?: number;
  clock?: DriveClock;
};

/** A Mass fitted into the trip. */
export type Stop = {
  candidate: Candidate;
  /** Arrive at the church. */
  arrive: number;
  /** Seated, `arriveEarlyMin` before Mass. */
  seatedBy: number;
  /** Mass over, back to the car. */
  leave: number;
  /** Minutes standing around before `seatedBy` (arrived early). */
  waitMin: number;
};

export type Plan = {
  depart: number;
  stops: Stop[];
  finish: number;
  /** Finish minus the finish of the same departure with no Masses, minutes. */
  addedMin: number;
  /**
   * Depart-at only: how much later you could leave and still make every
   * Mass, minutes. Waiting at church is time you could have spent at home.
   */
  slackMin: number;
};

const minutesBetween = (later: number, earlier: number) => (later - earlier) / MINUTE;

type Settings = Required<Omit<TripInput, "constraint">> & { constraint: Constraint };

function settle(input: TripInput): Settings {
  return {
    totalMin: input.totalMin,
    constraint: input.constraint,
    marginMin: input.marginMin ?? 0,
    arriveEarlyMin: input.arriveEarlyMin ?? ARRIVE_EARLY_MIN,
    maxWaitMin: input.maxWaitMin ?? Infinity,
    clock: input.clock ?? NONSTOP,
  };
}

const seatedBy = (c: Candidate, s: Settings) => c.start - s.arriveEarlyMin * MINUTE;
const release = (c: Candidate) => c.start + c.minutes * MINUTE;
const fromStart = (c: Candidate, s: Settings) => c.alongMin + c.toMin + s.marginMin;
const toFinish = (c: Candidate, s: Settings) => c.backMin + Math.max(0, s.totalMin - c.alongMin) + s.marginMin;

/** Drive minutes between two Masses, via the route (analysis §3). */
export const between = (a: Candidate, b: Candidate, marginMin = 0): number =>
  a.churchId === b.churchId ? 0 : a.backMin + Math.abs(b.alongMin - a.alongMin) + b.toMin + marginMin;

/** Build the timed plan for a chosen sequence of Masses from a departure time. */
function timePlan(depart: number, chosen: Candidate[], s: Settings): Plan {
  const stops: Stop[] = [];
  let now = depart;
  let previous: Candidate | null = null;
  for (const candidate of chosen) {
    const drive = previous ? between(previous, candidate, s.marginMin) : fromStart(candidate, s);
    const arrive = s.clock.forward(now, drive);
    const seated = seatedBy(candidate, s);
    stops.push({ candidate, arrive, seatedBy: seated, leave: release(candidate), waitMin: minutesBetween(seated, arrive) });
    now = release(candidate);
    previous = candidate;
  }
  const finish = previous ? s.clock.forward(now, toFinish(previous, s)) : s.clock.forward(depart, s.totalMin + s.marginMin);
  const direct = s.clock.forward(depart, s.totalMin + s.marginMin);

  let latest = Infinity;
  // Latest departure that still makes the first Mass; later Masses are fixed in time.
  if (chosen[0]) latest = s.clock.backward(seatedBy(chosen[0], s), fromStart(chosen[0], s));
  return {
    depart,
    stops,
    finish,
    addedMin: minutesBetween(finish, direct),
    slackMin: Number.isFinite(latest) ? Math.max(0, minutesBetween(latest, depart)) : 0,
  };
}

/**
 * Every single-Mass option, best first.
 *
 * Depart at: feasible when you can be seated in time; ranked by added trip
 * time (detour + wait, since the Mass block itself is the same for all
 * Masses of a kind), then by finish.
 *
 * Arrive by: feasible when the trip still ends on time; ranked by latest
 * departure, then by earliest finish.
 */
export function rankSingleMass(candidates: Candidate[], input: TripInput): Plan[] {
  const s = settle(input);
  const plans: Plan[] = [];
  for (const candidate of candidates) {
    const seated = seatedBy(candidate, s);
    if (s.constraint.kind === "depart") {
      const arrive = s.clock.forward(s.constraint.at, fromStart(candidate, s));
      if (arrive > seated || minutesBetween(seated, arrive) > s.maxWaitMin) continue;
      plans.push(timePlan(s.constraint.at, [candidate], s));
    } else {
      const finish = s.clock.forward(release(candidate), toFinish(candidate, s));
      if (finish > s.constraint.by) continue;
      const depart = s.clock.backward(seated, fromStart(candidate, s));
      plans.push(timePlan(depart, [candidate], s));
    }
  }
  if (s.constraint.kind === "depart") {
    plans.sort((a, b) => a.addedMin - b.addedMin || a.finish - b.finish);
  } else {
    plans.sort((a, b) => b.depart - a.depart || a.finish - b.finish);
  }
  return plans;
}

/** The trip with no Mass, for comparison and as the fallback. */
export function directPlan(input: TripInput): Plan {
  const s = settle(input);
  const depart =
    s.constraint.kind === "depart" ? s.constraint.at : s.clock.backward(s.constraint.by, s.totalMin + s.marginMin);
  return timePlan(depart, [], s);
}

type Label = {
  count: number;
  /** Arrive-by: the departure this chain forces (set by its first Mass). Depart-at: unused. */
  depart: number;
  driveMin: number;
  prev: number;
};

/** True when label `a` is strictly better than `b` under the constraint's ranking. */
function better(a: Label, b: Label | undefined, arrive: boolean): boolean {
  if (!b) return true;
  if (a.count !== b.count) return a.count > b.count;
  if (arrive && a.depart !== b.depart) return a.depart > b.depart;
  return a.driveMin < b.driveMin;
}

/**
 * Plan one Mass per distinct `dayKey`, as many days as the trip allows
 * (analysis §3): the most Masses first, then the earliest finish (depart at)
 * or latest departure (arrive by), then the least driving.
 *
 * Exact: a forward DAG pass over candidates in start order. Each candidate's
 * times are fixed by its Mass, so the best chain into it does not depend on
 * what comes after it.
 */
export function planTrip(candidates: Candidate[], input: TripInput): Plan {
  const s = settle(input);
  const arrive = s.constraint.kind === "arrive";
  const nodes = [...candidates].sort((a, b) => a.start - b.start);
  const labels: (Label | undefined)[] = new Array(nodes.length);

  for (let j = 0; j < nodes.length; j++) {
    const node = nodes[j]!;
    const seated = seatedBy(node, s);

    // Start the chain here, straight from the trip's origin.
    if (s.constraint.kind === "depart") {
      const arrive = s.clock.forward(s.constraint.at, fromStart(node, s));
      if (arrive <= seated && minutesBetween(seated, arrive) <= s.maxWaitMin) {
        labels[j] = { count: 1, depart: s.constraint.at, driveMin: fromStart(node, s), prev: -1 };
      }
    } else {
      const depart = s.clock.backward(seated, fromStart(node, s));
      labels[j] = { count: 1, depart, driveMin: fromStart(node, s), prev: -1 };
    }

    // Or extend an earlier chain.
    for (let i = 0; i < j; i++) {
      const from = labels[i];
      const prior = nodes[i]!;
      if (!from || prior.dayKey >= node.dayKey) continue;
      const drive = between(prior, node, s.marginMin);
      if (s.clock.forward(release(prior), drive) > seated) continue;
      const candidate: Label = { count: from.count + 1, depart: from.depart, driveMin: from.driveMin + drive, prev: i };
      if (better(candidate, labels[j], arrive)) labels[j] = candidate;
    }
  }

  // Close each chain at the destination and keep the best feasible one.
  let best: { index: number; finish: number; label: Label } | null = null;
  for (let j = 0; j < nodes.length; j++) {
    const label = labels[j];
    if (!label) continue;
    const node = nodes[j]!;
    const finish = s.clock.forward(release(node), toFinish(node, s));
    if (s.constraint.kind === "arrive" && finish > s.constraint.by) continue;
    const closed: Label = { ...label, driveMin: label.driveMin + toFinish(node, s) };
    if (
      !best ||
      closed.count > best.label.count ||
      (closed.count === best.label.count &&
        (arrive
          ? closed.depart > best.label.depart || (closed.depart === best.label.depart && finish < best.finish)
          : finish < best.finish || (finish === best.finish && closed.driveMin < best.label.driveMin)))
    ) {
      best = { index: j, finish, label: closed };
    }
  }

  if (!best) return directPlan(input);
  const chain: Candidate[] = [];
  for (let index = best.index; index !== -1; index = labels[index]!.prev) chain.unshift(nodes[index]!);
  const depart = s.constraint.kind === "depart" ? s.constraint.at : best.label.depart;
  return timePlan(depart, chain, s);
}

/** Days in `required` that the plan has no Mass for. */
export function missedDays(plan: Plan, required: DateKey[]): DateKey[] {
  const covered = new Set(plan.stops.map((stop) => stop.candidate.dayKey));
  return required.filter((day) => !covered.has(day));
}
